import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Check, Copy, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'lumera_nl_state';
const COOLDOWN_DAYS = 7;

const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
  };
};

const getStoredState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch { return {}; }
};

const setStoredState = (state) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const NewsletterPopup = ({ isAuthenticated = false }) => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState('form'); // form | loading | success
  const [email, setEmail] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const stored = getStoredState();

    // Already subscribed? Don't show.
    if (stored.subscribed) return;

    // Dismissed recently? Check cooldown.
    if (stored.dismissed) {
      const dismissedAt = new Date(stored.dismissed);
      const now = new Date();
      const diffDays = (now - dismissedAt) / (1000 * 60 * 60 * 24);
      if (diffDays < COOLDOWN_DAYS) return;
    }

    // Don't show to authenticated users
    if (isAuthenticated) return;

    // Don't show on checkout/login/register/app/payment paths
    const path = window.location.pathname;
    if (['/login', '/register', '/checkout', '/verify', '/profile', '/dashboard', '/generator', '/personalize', '/customize', '/templates', '/pricing', '/payment'].some(p => path.startsWith(p))) return;

    // Show after delay + scroll
    let showTimeout;
    let hasScrolled = false;

    const handleScroll = () => {
      if (window.scrollY > 300) hasScrolled = true;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    showTimeout = setTimeout(() => {
      // Show after 6s if scrolled, or after 10s regardless
      if (hasScrolled) {
        setVisible(true);
      } else {
        setTimeout(() => setVisible(true), 4000);
      }
    }, 6000);

    return () => {
      clearTimeout(showTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isAuthenticated]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setStoredState({ ...getStoredState(), dismissed: new Date().toISOString() });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }

    setStep('loading');
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email: trimmed,
        ...getUtmParams(),
        source: 'popup',
      });
      setCouponCode(res.data.coupon_code);
      setStep('success');
      setStoredState({ ...getStoredState(), subscribed: true, email: trimmed });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setStep('form');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(couponCode);
    setCopied(true);
    toast.success('Discount code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!visible || isAuthenticated) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="newsletter-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-[420px] bg-[#FDFBF8] rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          data-testid="newsletter-close"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-stone-400 hover:text-stone-700 transition-colors shadow-sm"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#B49A88] via-[#d4c0b0] to-[#B49A88]" />

        <div className="px-8 py-10 text-center">
          {step === 'form' && (
            <>
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#B49A88]/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#B49A88]" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-stone-900 mb-2 leading-tight">
                Get 10% off your first product
              </h2>
              <p className="text-sm font-sans text-stone-500 mb-6 leading-relaxed">
                Join the Lumera list for new planners, inspiration, updates and exclusive offers.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3.5 bg-white border border-stone-200 rounded-xl text-sm font-sans text-stone-800 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] focus:ring-1 focus:ring-[#B49A88]/30 transition-all"
                  autoComplete="email"
                />
                {error && (
                  <p className="text-xs font-sans text-red-500 text-left">{error}</p>
                )}
                <Button
                  data-testid="newsletter-submit"
                  type="submit"
                  className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-xl py-3.5 text-sm font-sans font-semibold transition-all"
                >
                  Get my 10% off
                </Button>
              </form>

              <p className="text-[10px] font-sans text-stone-400 mt-4 leading-relaxed">
                By subscribing, you agree to receive marketing emails from Lumera. You can unsubscribe anytime.
              </p>
            </>
          )}

          {step === 'loading' && (
            <div className="py-8">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#B49A88]" />
              <p className="text-sm font-sans text-stone-500 mb-4">Registering...</p>
            </div>
          )}

          {step === 'success' && (
            <>
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-emerald-50 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-500" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-stone-900 mb-2">
                You're in
              </h2>
              <p className="text-sm font-sans text-stone-500 mb-6">
                Your 10% discount is ready. We also sent it to your email.
              </p>

              {/* Coupon code box */}
              <div className="bg-[#faf8f5] border border-[#e7e0d8] rounded-xl p-5 mb-5">
                <p className="text-[10px] font-sans text-[#B49A88] uppercase tracking-widest mb-2">Your discount code</p>
                <div className="flex items-center justify-center gap-3">
                  <span data-testid="newsletter-coupon-code" className="font-mono text-xl font-bold text-stone-900 tracking-wider">
                    {couponCode}
                  </span>
                  <button
                    data-testid="newsletter-copy-code"
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-sans font-semibold text-stone-600 hover:bg-stone-50 transition-colors"
                  >
                    {copied ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <p className="text-[10px] font-sans text-stone-400 mt-2">Apply at checkout · Valid for individual products</p>
              </div>

              <Button
                data-testid="newsletter-browse"
                onClick={() => { handleClose(); window.location.hash = ''; window.scrollTo({ top: 600, behavior: 'smooth' }); }}
                className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-xl py-3.5 text-sm font-sans font-semibold"
              >
                Start shopping <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
